import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { v2Api } from "@/lib/v2-api/client";
import {
  processDocumentBlob,
  getFileType,
  type ProcessedDoc,
} from "@/app/chat/hooks/useDocumentBlob";

/**
 * Source-document preview for an AI proposal batch.
 *
 * The metadata query (does a source doc exist? its name/url) is always run
 * when a batchId is present — it's a cheap DB lookup + presign and powers the
 * "view source" affordance on every card. The blob download + decode (which
 * pulls the full .xlsx and converts it to an ArrayBuffer) is gated on
 * `downloadBlob` so it only happens when the slide-over is actually open —
 * otherwise every card in the queue would eagerly fetch its mercuriale.
 */
export function useChatMediaPreview(
  batchId: string | null,
  downloadBlob: boolean,
) {
  const srcQuery = useQuery({
    queryKey: ["ai-proposal-source-doc", batchId],
    queryFn: () => v2Api.proposals.getSourceDocument(batchId!),
    enabled: !!batchId,
    staleTime: 30 * 60 * 1000,
  });

  const [doc, setDoc] = useState<ProcessedDoc | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const src = srcQuery.data;
    if (!src || !downloadBlob) {
      setDoc(null);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const blob = await (await fetch(src.url)).blob();
        if (cancelled) return;
        const processed = await processDocumentBlob(blob, src.name);
        url = processed.documentBlobUrl;
        if (cancelled) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        setDoc(processed);
      } catch {
        if (!cancelled) setError("Failed to load document");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [srcQuery.data, downloadBlob]);

  return {
    source: srcQuery.data ?? null,
    hasSource: !!srcQuery.data,
    doc,
    loading: downloadBlob && (loading || srcQuery.isLoading),
    error,
    getFileType,
  };
}
